/**
 * Coût des articles (variantes Shopify) saisi dans le dashboard : lecture et
 * saisie. Une écriture renvoie la liste entière, posée dans le cache, et
 * invalide les statistiques de ventes : le COGS de chaque page en dépend.
 */

import type { ArticleCost, ArticleCostInput } from '@shared/types/settings';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiRequestError, fetchArticleCosts, putArticleCost } from '../lib/api';
import { useAuth } from './useAuth';

const ARTICLE_COSTS_KEY = ['article-costs'] as const;

export function useArticleCosts() {
  const { token, signOut } = useAuth();
  return useQuery<ArticleCost[], Error>({
    queryKey: ARTICLE_COSTS_KEY,
    queryFn: async ({ signal }) => {
      if (!token) throw new ApiRequestError(401, 'Session expiree');
      try {
        return (await fetchArticleCosts(token, signal)).articles;
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) signOut();
        throw error;
      }
    },
    enabled: Boolean(token),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) =>
      !(error instanceof ApiRequestError && error.status >= 400 && error.status < 500) && failureCount < 2,
  });
}

export function useWriteArticleCost() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  return useMutation<ArticleCost[], Error, { variantId: string; input: ArticleCostInput }>({
    mutationFn: async ({ variantId, input }) => {
      if (!token) throw new ApiRequestError(401, 'Session expiree');
      return (await putArticleCost(variantId, input, token)).articles;
    },
    onSuccess: async (articles) => {
      queryClient.setQueryData(ARTICLE_COSTS_KEY, articles);
      await queryClient.invalidateQueries({ predicate: (query) => /^(sales|btob)/.test(String(query.queryKey[0])) });
    },
  });
}
